import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Rocket, Loader2, Plus, X, Play, Square, ArrowUpDown } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Environment = 'production' | 'staging' | 'development';
type DeploymentStatus = 'active' | 'provisioning' | 'failed' | 'stopped';

interface Deployment {
  id: string;
  name: string;
  model_id: string;
  model_name: string;
  environment: Environment;
  status: DeploymentStatus;
  resources: { cpu: string; memory: string; gpu: string };
  auto_scaling: boolean;
  created_at: string;
}

interface DeploymentsResponse {
  deployments: Deployment[];
  total: number;
  active: number;
  failed: number;
  pending: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ENVIRONMENTS: Environment[] = ['production', 'staging', 'development'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function envBadge(env: Environment) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (env) {
    case 'production':
      return <span className={`${base} bg-red-500/15 text-red-500`}>Production</span>;
    case 'staging':
      return <span className={`${base} bg-yellow-500/15 text-yellow-500`}>Staging</span>;
    case 'development':
      return <span className={`${base} bg-blue-500/15 text-blue-500`}>Development</span>;
  }
}

function statusBadge(status: DeploymentStatus) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (status) {
    case 'active':
      return <span className={`${base} bg-green-500/15 text-green-500`}>Active</span>;
    case 'provisioning':
      return <span className={`${base} bg-blue-500/15 text-blue-500`}>Provisioning</span>;
    case 'failed':
      return <span className={`${base} bg-red-500/15 text-red-500`}>Failed</span>;
    case 'stopped':
      return <span className={`${base} bg-muted text-muted-foreground`}>Stopped</span>;
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function DeploymentsPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [modelId, setModelId] = useState('');
  const [environment, setEnvironment] = useState<Environment>('development');
  const [cpu, setCpu] = useState('2');
  const [memory, setMemory] = useState('4Gi');
  const [gpu, setGpu] = useState('0');
  const [autoScaling, setAutoScaling] = useState(false);

  const deploymentsQuery = useQuery<DeploymentsResponse>({
    queryKey: ['governance', 'deployments'],
    queryFn: () => api<DeploymentsResponse>('governance/models', { query: { scope: 'deployments' } }),
  });

  const provisionMutation = useMutation({
    mutationFn: (body: { name: string; model_id: string; environment: Environment; resources: { cpu: string; memory: string; gpu: string }; auto_scaling: boolean }) =>
      api<Deployment>('governance/models', { body: { ...body, action: 'provision' } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governance', 'deployments'] });
      resetForm();
    },
  });

  const actionMutation = useMutation({
    mutationFn: (payload: { id: string; action: 'start' | 'stop' | 'scale' }) =>
      api<Deployment>(`governance/models`, { method: 'PUT', body: { scope: 'deployments', ...payload } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governance', 'deployments'] });
    },
  });

  function resetForm() {
    setShowForm(false);
    setName('');
    setModelId('');
    setEnvironment('development');
    setCpu('2');
    setMemory('4Gi');
    setGpu('0');
    setAutoScaling(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !modelId.trim()) return;
    provisionMutation.mutate({
      name: name.trim(),
      model_id: modelId.trim(),
      environment,
      resources: { cpu, memory, gpu },
      auto_scaling: autoScaling,
    });
  }

  const deployments = deploymentsQuery.data?.deployments ?? [];
  const stats = deploymentsQuery.data;

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Rocket className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">AI Deployments</h1>
            <p className="text-muted-foreground mt-1">Provision, monitor, and manage AI model deployments across environments.</p>
          </div>
        </div>
        {!showForm && (
          <button type="button" onClick={() => setShowForm(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 flex-shrink-0">
            <Plus className="h-4 w-4" />
            New Deployment
          </button>
        )}
      </div>

      {/* Stats */}
      {deploymentsQuery.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Total Deployments</p>
            <p className="text-2xl font-bold text-foreground">{stats?.total ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Active</p>
            <p className="text-2xl font-bold text-green-500">{stats?.active ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Failed</p>
            <p className="text-2xl font-bold text-red-500">{stats?.failed ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Pending</p>
            <p className="text-2xl font-bold text-blue-500">{stats?.pending ?? 0}</p>
          </div>
        </div>
      )}

      {/* Provision form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-card p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Provision New Deployment</h3>
            <button type="button" onClick={resetForm} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Deployment Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. gpt4o-prod-east" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Model ID</label>
              <input value={modelId} onChange={(e) => setModelId(e.target.value)} required placeholder="Model identifier" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Environment</label>
              <select value={environment} onChange={(e) => setEnvironment(e.target.value as Environment)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground">
                {ENVIRONMENTS.map((e) => <option key={e} value={e}>{e.charAt(0).toUpperCase() + e.slice(1)}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Resources</label>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">CPU Cores</label>
                <input value={cpu} onChange={(e) => setCpu(e.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Memory</label>
                <input value={memory} onChange={(e) => setMemory(e.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">GPU</label>
                <input value={gpu} onChange={(e) => setGpu(e.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-foreground">
            <input type="checkbox" checked={autoScaling} onChange={(e) => setAutoScaling(e.target.checked)} className="h-4 w-4 rounded border-border text-primary focus:ring-primary" />
            Enable auto-scaling
          </label>

          {provisionMutation.isError && (
            <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-500">
              {provisionMutation.error instanceof Error ? provisionMutation.error.message : 'Failed to provision deployment.'}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button type="submit" disabled={provisionMutation.isPending || !name.trim() || !modelId.trim()} className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
              {provisionMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {provisionMutation.isPending ? 'Provisioning...' : 'Provision'}
            </button>
            <button type="button" onClick={resetForm} className="px-4 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">Cancel</button>
          </div>
        </form>
      )}

      {/* Deployment table */}
      {deploymentsQuery.isLoading && (
        <div className="rounded-lg border border-border bg-card p-12 flex items-center justify-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="text-muted-foreground">Loading deployments...</span>
        </div>
      )}

      {deploymentsQuery.isError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-6 text-center">
          <p className="text-sm text-red-500">
            Failed to load deployments.{' '}
            <button type="button" onClick={() => deploymentsQuery.refetch()} className="underline hover:no-underline">Retry</button>
          </p>
        </div>
      )}

      {deploymentsQuery.isSuccess && deployments.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground">
          No deployments yet. Click "New Deployment" to provision your first model.
        </div>
      )}

      {deploymentsQuery.isSuccess && deployments.length > 0 && (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="flex items-center gap-4 px-4 py-2 bg-muted/50 border-b border-border text-xs font-medium text-muted-foreground uppercase tracking-wider">
            <span className="flex-1">Name</span>
            <span className="w-28">Model</span>
            <span className="w-24">Environment</span>
            <span className="w-24">Status</span>
            <span className="w-28">Created</span>
            <span className="w-32">Actions</span>
          </div>
          {deployments.map((dep) => (
            <div key={dep.id} className="flex items-center gap-4 px-4 py-3 border-b border-border last:border-b-0 hover:bg-accent/50">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{dep.name}</p>
                {dep.auto_scaling && <p className="text-xs text-muted-foreground">Auto-scaling enabled</p>}
              </div>
              <span className="w-28 text-sm text-foreground truncate">{dep.model_name}</span>
              <span className="w-24">{envBadge(dep.environment)}</span>
              <span className="w-24">{statusBadge(dep.status)}</span>
              <span className="w-28 text-sm text-muted-foreground">{formatDate(dep.created_at)}</span>
              <div className="w-32 flex items-center gap-1">
                {dep.status === 'stopped' && (
                  <button
                    type="button"
                    onClick={() => actionMutation.mutate({ id: dep.id, action: 'start' })}
                    disabled={actionMutation.isPending}
                    className="p-1.5 rounded-md text-green-500 hover:bg-green-500/10 transition-colors"
                    title="Start"
                  >
                    <Play className="h-3.5 w-3.5" />
                  </button>
                )}
                {dep.status === 'active' && (
                  <>
                    <button
                      type="button"
                      onClick={() => actionMutation.mutate({ id: dep.id, action: 'stop' })}
                      disabled={actionMutation.isPending}
                      className="p-1.5 rounded-md text-red-500 hover:bg-red-500/10 transition-colors"
                      title="Stop"
                    >
                      <Square className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => actionMutation.mutate({ id: dep.id, action: 'scale' })}
                      disabled={actionMutation.isPending}
                      className="p-1.5 rounded-md text-blue-500 hover:bg-blue-500/10 transition-colors"
                      title="Scale"
                    >
                      <ArrowUpDown className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
